
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Loader2, Upload, Download, CheckCircle, AlertCircle, BookOpen, ArrowLeft, Camera } from "lucide-react";
import { useToast } from '@/hooks/use-toast';
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from 'react-router-dom';

interface ProcessedDocument {
  originalName: string;
  translation: string;
  description: string;
  solution: string;
}

const ProcesaDocumentos = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedDoc, setProcessedDoc] = useState<ProcessedDocument | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (!file) return;

    const isValidType = file.type === 'application/pdf' || 
                       file.type.startsWith('image/') ||
                       file.type === 'application/msword' ||
                       file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    
    const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
    
    if (!isValidType) {
      toast({
        variant: "destructive",
        title: "Tipo de archivo no válido",
        description: "Por favor selecciona un archivo PDF, DOC, DOCX o imagen",
      });
      return;
    }
    
    if (!isValidSize) {
      toast({
        variant: "destructive",
        title: "Archivo muy grande",
        description: "El archivo no puede exceder 10MB",
      });
      return;
    }

    setSelectedFile(file);
    setProcessedDoc(null);
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const result = reader.result as string;
          const base64String = result.split(',')[1];
          resolve(base64String);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsDataURL(file);
    });
  };

  const processDocument = async () => {
    if (!selectedFile) {
      toast({
        variant: "destructive",
        title: "No hay archivo",
        description: "Selecciona un archivo para procesar",
      });
      return;
    }

    setIsProcessing(true);

    try {
      console.log('Starting document processing for:', selectedFile.name);
      
      const base64File = await convertFileToBase64(selectedFile);
      
      console.log('File converted to base64, calling edge function...');

      const { data, error } = await supabase.functions.invoke('process-document', {
        body: {
          file: base64File,
          filename: selectedFile.name,
          fileType: selectedFile.type,
        },
      });

      console.log('Edge function response received');

      if (error) {
        console.error('Supabase function error:', error);
        throw new Error(error.message || 'Error en la función de procesamiento');
      }

      if (data && typeof data === 'object') {
        const processedResult: ProcessedDocument = {
          originalName: selectedFile.name,
          translation: data.translation || "No se pudo traducir el documento",
          description: data.description || "No se pudo analizar el documento",
          solution: data.solution || "No se encontraron soluciones específicas",
        };

        setProcessedDoc(processedResult);

        toast({
          title: "¡Procesamiento completado!",
          description: "Tu imagen ha sido procesada exitosamente",
        });
      } else {
        throw new Error('Respuesta inválida del servidor');
      }
    } catch (error) {
      console.error('Error processing document:', error);
      
      let errorMessage = "Error al procesar la imagen. Intenta nuevamente.";
      
      if (error instanceof Error) {
        if (error.message.includes('call stack')) {
          errorMessage = "Error interno del sistema. Intenta con una imagen más pequeña.";
        } else if (error.message.includes('timeout')) {
          errorMessage = "El procesamiento tomó demasiado tiempo. Intenta con una imagen más pequeña.";
        } else if (error.message.includes('network')) {
          errorMessage = "Error de conexión. Verifica tu internet e intenta nuevamente.";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadResults = () => {
    if (!processedDoc) return;

    const content = `
IMAGEN PROCESADA: ${processedDoc.originalName}

QUÉ DICE TU CARTA:
${processedDoc.translation}

DESCRIPCIÓN:
${processedDoc.description}

PASOS A SEGUIR:
${processedDoc.solution}

---
Procesado por MigranteApp - ${new Date().toLocaleDateString()}
    `;

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `procesado-${processedDoc.originalName}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/chat')}
            variant="ghost"
            className="mb-4 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al Chat
          </Button>
          
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Procesa tus Documentos
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Toma una foto de tu carta o documento y te ayudamos a entenderla
            </p>
          </div>
        </div>

        {/* Main Content */}
        <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-6">
            <CardTitle className="text-2xl flex items-center justify-center gap-3">
              <div className="p-3 bg-blue-100 rounded-full">
                <Camera className="h-8 w-8 text-blue-600" />
              </div>
              Sube tu Imagen
            </CardTitle>
          </CardHeader>
          
          <CardContent className="space-y-8">
            {/* Upload Section */}
            <div className="border-3 border-dashed border-blue-300 rounded-xl p-8 text-center bg-blue-50/50 hover:bg-blue-50 transition-colors">
              <div className="mb-6">
                <div className="inline-flex p-4 bg-blue-100 rounded-full mb-4">
                  <Camera className="h-12 w-12 text-blue-600" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                  Toma una foto de tu carta
                </h3>
                <p className="text-gray-600 mb-6">
                  Acepta fotos JPG, PNG, PDF • Máximo 10MB
                </p>
              </div>
              
              <Input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                className="hidden"
                id="document-upload"
              />
              
              <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg">
                <label htmlFor="document-upload" className="cursor-pointer">
                  <Camera className="mr-3 h-5 w-5" />
                  Seleccionar Imagen
                </label>
              </Button>
            </div>

            {/* Selected File Display */}
            {selectedFile && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <Camera className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-blue-900">{selectedFile.name}</p>
                        <p className="text-sm text-blue-600">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={processDocument}
                      disabled={isProcessing}
                      size="lg"
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-3"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Analizando...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="mr-2 h-5 w-5" />
                          Analizar Imagen
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Results Section */}
            {processedDoc && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-full">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-green-800">¡Imagen Procesada!</h2>
                  </div>
                  <Button onClick={downloadResults} variant="outline" size="lg" className="border-green-600 text-green-600 hover:bg-green-50">
                    <Download className="mr-2 h-5 w-5" />
                    Descargar Resultados
                  </Button>
                </div>

                <div className="grid gap-6">
                  {/* Translation Card */}
                  <Card className="border-l-4 border-l-blue-500 shadow-lg">
                    <CardHeader className="bg-blue-50">
                      <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
                        <BookOpen className="h-5 w-5" />
                        Qué Dice tu Carta
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <div className="bg-blue-50 p-4 rounded-lg border">
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{processedDoc.translation}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Description Card */}
                  <Card className="border-l-4 border-l-green-500 shadow-lg">
                    <CardHeader className="bg-green-50">
                      <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                        <AlertCircle className="h-5 w-5" />
                        Qué Significa
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <div className="bg-green-50 p-4 rounded-lg border">
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{processedDoc.description}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Solution Card */}
                  <Card className="border-l-4 border-l-orange-500 shadow-lg">
                    <CardHeader className="bg-orange-50">
                      <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
                        <CheckCircle className="h-5 w-5" />
                        Pasos a Seguir
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <div className="bg-orange-50 p-4 rounded-lg border">
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{processedDoc.solution}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProcesaDocumentos;
